/**
 * Pure model for Invariant Relay.
 *
 * The player advances an invariant through its lifecycle in order. The
 * retroactive-fix shortcut is not a generated move: a later repair cannot bless
 * an earlier lifecycle stage.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import levelsData from "../data/invariantRelayLevels.json" with { type: "json" };

export type InvariantRelayMoveKind =
  | "establish"
  | "preserve"
  | "recover"
  | "promote";

export interface InvariantRelayStage {
  readonly id: string;
  readonly label: string;
  readonly move: InvariantRelayMoveKind;
  readonly effect: string;
  readonly integrityDelta: number;
}

export interface InvariantRelayLevel {
  readonly id: string;
  readonly name: string;
  readonly brief: string;
  readonly initialIntegrity: number;
  readonly stages: readonly InvariantRelayStage[];
}

export type InvariantRelayStatus = "playing" | "cleared" | "stalled";

export interface InvariantRelayState {
  readonly levelId: string;
  readonly levelName: string;
  readonly brief: string;
  readonly stageIndex: number;
  readonly completedStageIds: readonly string[];
  readonly established: boolean;
  readonly preserved: boolean;
  readonly checkpointReady: boolean;
  readonly promoted: boolean;
  readonly integrity: number;
  readonly status: InvariantRelayStatus;
  readonly lastEvent: string;
}

export interface InvariantRelayMove {
  readonly kind: InvariantRelayMoveKind;
  readonly stageId: string;
  readonly label: string;
  readonly effect: string;
  readonly integrityDelta: number;
}

interface InvariantRelayLevelsData {
  readonly levels: readonly InvariantRelayLevel[];
}

export const INVARIANT_RELAY_LEVELS = (levelsData as InvariantRelayLevelsData)
  .levels;
export const DEFAULT_INVARIANT_RELAY_LEVEL_ID =
  INVARIANT_RELAY_LEVELS[0]?.id ?? "";

const LEVEL_BY_ID = new Map(
  INVARIANT_RELAY_LEVELS.map((level) => [level.id, level]),
);

export function getInvariantRelayLevel(levelId: string): InvariantRelayLevel {
  const level = LEVEL_BY_ID.get(levelId);
  if (!level) throw new Error(`Unknown Invariant Relay level: ${levelId}`);
  return level;
}

export function createInvariantRelayState(
  levelId = DEFAULT_INVARIANT_RELAY_LEVEL_ID,
): InvariantRelayState {
  const level = getInvariantRelayLevel(levelId);
  return {
    levelId: level.id,
    levelName: level.name,
    brief: level.brief,
    stageIndex: 0,
    completedStageIds: [],
    established: false,
    preserved: false,
    checkpointReady: false,
    promoted: false,
    integrity: level.initialIntegrity,
    status: "playing",
    lastEvent:
      "Start at the first lifecycle gate. Only the next valid gate is playable.",
  };
}

export function currentInvariantRelayStage(
  state: InvariantRelayState,
): InvariantRelayStage | undefined {
  return getInvariantRelayLevel(state.levelId).stages[state.stageIndex];
}

export function legalInvariantRelayMoves(
  state: InvariantRelayState,
): readonly InvariantRelayMove[] {
  if (state.status !== "playing") return [];
  const stage = currentInvariantRelayStage(state);
  if (!stage) return [];

  if (stage.move === "preserve" && !state.established) return [];
  if (stage.move === "recover" && (!state.established || !state.preserved)) {
    return [];
  }
  if (
    stage.move === "promote" &&
    (!state.established || !state.preserved || !state.checkpointReady)
  ) {
    return [];
  }

  return [
    {
      kind: stage.move,
      stageId: stage.id,
      label: stage.label,
      effect: stage.effect,
      integrityDelta: stage.integrityDelta,
    },
  ];
}

export function retroactiveFixMoveGenerated(): boolean {
  return false;
}

export function stepInvariantRelay(
  state: InvariantRelayState,
  move: InvariantRelayMove,
): InvariantRelayState {
  const legal = legalInvariantRelayMoves(state).some(
    (candidate) =>
      candidate.kind === move.kind && candidate.stageId === move.stageId,
  );
  if (!legal) return state;

  const nextIndex = state.stageIndex + 1;
  const level = getInvariantRelayLevel(state.levelId);
  const completedStageIds = [...state.completedStageIds, move.stageId];
  const next: InvariantRelayState = {
    ...state,
    stageIndex: nextIndex,
    completedStageIds,
    established: state.established || move.kind === "establish",
    preserved: state.preserved || move.kind === "preserve",
    checkpointReady: state.checkpointReady || move.kind === "recover",
    promoted: state.promoted || move.kind === "promote",
    integrity: Math.min(100, state.integrity + move.integrityDelta),
    lastEvent: `${move.label}: ${move.effect}`,
  };

  if (nextIndex >= level.stages.length) {
    return {
      ...next,
      status: "cleared",
      lastEvent:
        "Lifecycle complete. The invariant was carried forward without a retroactive fix.",
    };
  }

  if (legalInvariantRelayMoves(next).length === 0) {
    return {
      ...next,
      status: "stalled",
      lastEvent:
        "The lifecycle stalled because the next gate is missing its prior evidence.",
    };
  }

  return next;
}

export function invariantRelayProgress(state: InvariantRelayState): number {
  const stages = getInvariantRelayLevel(state.levelId).stages.length;
  if (stages === 0) return 0;
  return Math.round((state.completedStageIds.length / stages) * 100);
}
